import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { formatDayLabel, isSameDay } from "../lib/format";
import type { Translate } from "../lib/i18n";
import type { ChatEntry } from "../openclaw/transcript";
import { groupAnchor, groupTurns, summarizeSteps } from "../openclaw/turns";
import { useLocale, useT } from "../lib/useT";
import { conversationTitle, useStore, type QueueMode } from "../state/store";
import { Composer } from "./Composer";
import { MessageBubble, StreamingBubble } from "./MessageBubble";
import { Sheet } from "./Sheet";
import { TurnSteps } from "./TurnSteps";
import { IconBack, IconChevronDown, IconMore, IconSteps } from "./icons";

/** Distance from the bottom within which new output should keep the view pinned. */
const STICK_THRESHOLD_PX = 120;

export function ChatView({
  onBack,
  onMenu,
  onModel,
}: {
  onBack: () => void;
  onMenu: () => void;
  onModel: () => void;
}) {
  const activeKey = useStore((state) => state.activeKey);
  const conversations = useStore((state) => state.conversations);
  const entriesByKey = useStore((state) => state.entries);
  const liveRuns = useStore((state) => state.liveRuns);
  const historyLoading = useStore((state) => state.historyLoading);
  const historyExhausted = useStore((state) => state.historyExhausted);
  const historyLoadingMore = useStore((state) => state.historyLoadingMore);
  const loadOlderMessages = useStore((state) => state.loadOlderMessages);
  const drafts = useStore((state) => state.drafts);
  const draftAttachments = useStore((state) => state.draftAttachments);
  const setDraft = useStore((state) => state.setDraft);
  const attachFiles = useStore((state) => state.attachFiles);
  const removeAttachment = useStore((state) => state.removeAttachment);
  const send = useStore((state) => state.send);
  const stopRun = useStore((state) => state.stopRun);
  const connected = useStore((state) => state.connectionState.phase === "connected");
  const t = useT();
  const locale = useLocale();

  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const [showJump, setShowJump] = useState(false);
  // Expanding a turn's steps is a peek: it lives only as long as the chat is
  // open, so returning later always finds the transcript tidy again.
  const [expandedTurns, setExpandedTurns] = useState<ReadonlySet<string>>(new Set());
  const [stepsMenu, setStepsMenu] = useState<string | null>(null);
  const [dropping, setDropping] = useState(false);
  const [queueMode, setQueueMode] = useState<QueueMode>("steer");

  const row = conversations.find((entry) => entry.key === activeKey);
  const entries: ChatEntry[] = activeKey ? (entriesByKey[activeKey] ?? []) : [];
  const run = activeKey ? liveRuns[activeKey] : undefined;
  const loading = activeKey ? historyLoading[activeKey] : false;
  const loadingMore = activeKey ? historyLoadingMore[activeKey] : false;
  const canLoadOlder = Boolean(activeKey) && !historyExhausted[activeKey ?? ""];
  const draft = activeKey ? (drafts[activeKey] ?? "") : "";
  const staged = activeKey ? (draftAttachments[activeKey] ?? []) : [];
  const busy = Boolean(run?.active || row?.hasActiveRun || row?.status === "running");

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const node = scrollRef.current;
    if (!node) {
      return;
    }
    node.scrollTo({ top: node.scrollHeight, behavior });
  }, []);

  const onScroll = useCallback(() => {
    const node = scrollRef.current;
    if (!node) {
      return;
    }
    const distance = node.scrollHeight - node.scrollTop - node.clientHeight;
    stickToBottom.current = distance <= STICK_THRESHOLD_PX;
    setShowJump(!stickToBottom.current);
  }, []);

  // Opening a conversation always starts at the newest message, without animation.
  useLayoutEffect(() => {
    stickToBottom.current = true;
    setShowJump(false);
    setExpandedTurns(new Set());
    setStepsMenu(null);
    scrollToBottom("auto");
  }, [activeKey, scrollToBottom]);

  // New output only moves the view when the reader is already at the bottom, so
  // scrolling back through history is never yanked away mid-read.
  useEffect(() => {
    if (stickToBottom.current) {
      scrollToBottom(entries.length ? "smooth" : "auto");
    }
  }, [entries.length, run?.text, run?.active, scrollToBottom]);

  if (!activeKey || !row) {
    // A conversation can vanish from under an open chat — archived, deleted, or
    // dropped out of the list. On a phone the list is hidden while a chat is
    // open, so without this header there would be no way back at all.
    return (
      <div className="pane-chat">
        {activeKey ? (
          <header className="header">
            <button
              type="button"
              className="icon-button chat-back"
              onClick={onBack}
              aria-label={t("chat.back")}
            >
              <IconBack />
            </button>
            <div className="header-title">
              <h1>{t("chat.overview")}</h1>
            </div>
          </header>
        ) : null}
        <div className="empty-state">
          <span className="empty-icon" aria-hidden="true">
            🦀
          </span>
          <h2>{activeKey ? t("chat.goneTitle") : t("chat.noneTitle")}</h2>
          <p>{activeKey ? t("chat.goneBody") : t("chat.noneBody")}</p>
        </div>
      </div>
    );
  }

  const title = conversationTitle(row);
  const groups = groupTurns(entries, Boolean(run?.active));
  const menuGroup = groups.find((group) => group.id === stepsMenu);
  const menuSteps = menuGroup?.kind === "turn" ? menuGroup.steps : [];
  const menuExpanded = stepsMenu ? expandedTurns.has(stepsMenu) : false;

  return (
    <div
      className={`pane-chat${dropping ? " is-dropping" : ""}`}
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes("Files")) {
          event.preventDefault();
          setDropping(true);
        }
      }}
      onDragLeave={(event) => {
        // Only the drag actually leaving the pane counts, not crossing a child.
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setDropping(false);
        }
      }}
      onDrop={(event) => {
        const files = [...event.dataTransfer.files];
        if (files.length) {
          event.preventDefault();
          void attachFiles(activeKey, files);
        }
        setDropping(false);
      }}
    >
      {dropping ? <div className="drop-hint">{t("chat.dropHint")}</div> : null}
      <header className="header">
        <button
          type="button"
          className="icon-button chat-back"
          onClick={onBack}
          aria-label={t("chat.back")}
        >
          <IconBack />
        </button>
        <div className="header-title">
          <h1>{title}</h1>
          <p>
            {busy ? t("chat.working") : connected ? t("chat.connected") : t("chat.disconnected")}
            {" · "}
            {/* The model is the one setting people change mid-conversation, so
                it is a control in the header rather than an entry three taps
                deep in a menu. */}
            <button type="button" className="header-model" onClick={onModel}>
              {row.model ?? t("chat.defaultModel")}
            </button>
          </p>
        </div>
        <button
          type="button"
          className="icon-button"
          onClick={onMenu}
          aria-label={t("chat.options")}
          title={t("chat.options")}
        >
          <IconMore />
        </button>
      </header>

      <div className="transcript" ref={scrollRef} onScroll={onScroll}>
        <div className="transcript-inner">
          {loading && !entries.length ? (
            <div className="empty-state">
              <span className="spinner" />
              <p>{t("chat.loadingHistory")}</p>
            </div>
          ) : null}

          {!loading && !entries.length && !run ? (
            <div className="empty-state">
              <span className="empty-icon" aria-hidden="true">
                ✨
              </span>
              <h2>{title}</h2>
              <p>{t("chat.emptyBody")}</p>
            </div>
          ) : null}

          {/* Paging up is explicit rather than automatic: an auto-load on scroll
              fights the reader who is deliberately going back through a long
              transcript. */}
          {entries.length && canLoadOlder ? (
            <button
              type="button"
              className="load-older"
              onClick={() => void loadOlderMessages(activeKey)}
              disabled={loadingMore}
            >
              {loadingMore ? t("chat.loadingOlder") : t("chat.loadOlder")}
            </button>
          ) : null}

          {groups.map((group, index) => {
            const anchor = groupAnchor(group);
            const previous = groupAnchor(groups[index - 1]);
            const showDivider =
              Boolean(anchor && anchor.timestamp > 0) &&
              (!previous || !isSameDay(previous.timestamp, anchor!.timestamp));
            return (
              <div key={group.id} style={{ display: "contents" }}>
                {showDivider && anchor ? (
                  <div className="day-divider">{formatDayLabel(anchor.timestamp, locale, t)}</div>
                ) : null}
                {group.kind === "single" ? (
                  <MessageBubble entry={group.entry} sessionKey={activeKey} />
                ) : (
                  <>
                    <TurnSteps
                      steps={group.steps}
                      expanded={expandedTurns.has(group.id)}
                      live={Boolean(run?.active) && index === groups.length - 1}
                      onMenu={() => setStepsMenu(group.id)}
                    />
                    {group.answer ? (
                      <MessageBubble entry={group.answer} sessionKey={activeKey} />
                    ) : null}
                  </>
                )}
              </div>
            );
          })}

          {run ? (
            <StreamingBubble
              text={run.text}
              phase={run.phase}
              errorMessage={run.errorMessage}
              failed={run.failed}
            />
          ) : null}
        </div>
      </div>

      {/* A zero-height slot right above the composer. The button hangs off its
          bottom edge, so it clears the composer at any height and its coming
          and going never shifts the transcript. */}
      <div className="jump-latest-slot">
        {showJump ? (
          <button
            type="button"
            className="jump-latest"
            onClick={() => {
              stickToBottom.current = true;
              setShowJump(false);
              scrollToBottom();
            }}
            aria-label={t("chat.jumpLatest")}
            title={t("chat.jumpLatest")}
          >
            <IconChevronDown size={20} />
          </button>
        ) : null}
      </div>

      <Composer
        value={draft}
        onChange={(value) => setDraft(activeKey, value)}
        onSend={() => void send(activeKey, draft, busy ? queueMode : undefined)}
        onStop={() => void stopRun(activeKey)}
        onAttach={(files) => void attachFiles(activeKey, files)}
        onRemoveAttachment={(id) => removeAttachment(activeKey, id)}
        attachments={staged}
        queueMode={queueMode}
        onQueueModeChange={setQueueMode}
        busy={busy}
        disabled={!connected}
        placeholder={connected ? t("chat.composerPlaceholder") : t("chat.composerDisconnected")}
      />

      {stepsMenu && menuGroup ? (
        <Sheet
          title={t("steps.title")}
          description={describeSteps(menuSteps, t)}
          onClose={() => setStepsMenu(null)}
        >
          <div className="sheet-menu">
            <button
              type="button"
              onClick={() => {
                const id = stepsMenu;
                setExpandedTurns((current) => {
                  const next = new Set(current);
                  if (next.has(id)) {
                    next.delete(id);
                  } else {
                    next.add(id);
                  }
                  return next;
                });
                setStepsMenu(null);
              }}
            >
              <IconSteps size={19} />
              {menuExpanded ? t("steps.hide") : t("steps.show")}
            </button>
          </div>
        </Sheet>
      ) : null}
    </div>
  );
}

/** "4 steps · Read a file, Web search" for the menu's subtitle. */
function describeSteps(steps: ChatEntry[], t: Translate): string {
  const { count, labels } = summarizeSteps(steps, t);
  const counted = count === 1 ? t("steps.one") : t("steps.many", { count });
  return labels.length ? `${counted} · ${labels.join(", ")}` : counted;
}
